<template>
    <TriadModal ref="modal" title="Set Song Background" size="medium">
        <div class="space-y-4">
            <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Image</label>
                <TriadFileInput
                    :id="playlistId"
                    direction="gallery"
                    @selected="onImageSelected"
                />
                <div v-if="selectedImage" class="mt-1">
                    <img :src="selectedImage" class="w-full h-40 object-cover rounded-lg" />
                </div>
            </div>
            
            <div class="flex items-center">
                <div class="flex-1 h-px bg-gray-300"></div>
                <span class="px-4 text-sm text-gray-500">or</span>
                <div class="flex-1 h-px bg-gray-300"></div>
            </div>
            
            <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Video URL (YouTube)</label>
                <div class="flex gap-2">
                    <input 
                        type="text" 
                        v-model="videoUrl" 
                        placeholder="https://www.youtube.com/watch?v=..."
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                        @input="onVideoInput"
                    />
                </div>
                <div v-if="videoPreview" class="mt-2">
                    <img :src="videoPreview" @error="videoPreview = null" class="w-full h-40 object-cover rounded-lg" />
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Video Transition</label>
                <div class="grid grid-cols-3 gap-2">
                    <button
                        v-for="transition in transitions"
                        :key="transition.value"
                        @click="selectedTransition = transition.value"
                        class="px-3 py-2 text-sm rounded-lg border-2 transition-all"
                        :class="selectedTransition === transition.value 
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'"
                    >
                        {{ transition.label }}
                    </button>
                </div>
            </div>

            <div class="flex gap-2 pt-2">
                <button 
                    v-if="videoPreview || existingBackground"
                    @click="setVideoBackground"
                    class="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    {{ existingBackground ? 'Update Background' : 'Apply Background' }}
                </button>
                <button 
                    v-if="existingBackground"
                    @click="removeVideoBackground"
                    class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                    Remove
                </button>
            </div>
        </div>
    </TriadModal>
</template>

<script setup>
import { ref, defineProps, defineEmits, defineExpose } from 'vue';
import TriadModal from '@/components/common/TriadModal.vue';
import TriadFileInput from '@/components/form/TriadFileInput.vue';
import { getVideoId, getYouTubeUrl } from '@/utils/youtube';

defineProps({
    playlistId: { type: String, required: true }
});

const emit = defineEmits(['select']);

const transitions = [
    { label: 'None', value: 'none' },
    { label: 'Fade', value: 'fade' },
    { label: 'Crossfade', value: 'crossfade' },
    { label: 'Slide Left', value: 'slide-left' },
    { label: 'Slide Right', value: 'slide-right' },
    { label: 'Zoom', value: 'zoom' },
];

const modal = ref(null);
const selectedImage = ref(null);
const videoUrl = ref('');
const videoPreview = ref(null);
const selectedTransition = ref('fade');
const existingBackground = ref(null);
const existingTransition = ref('fade');

const open = (options = {}) => {
    reset();
    if (options.background) {
        existingBackground.value = options.background;
        selectedTransition.value = options.transition || 'fade';
        existingTransition.value = options.transition || 'fade';
        // Youtube vai para o campo de vídeo (com thumb); imagem mostra na seção Image
        if (getVideoId(options.background)) {
            videoUrl.value = getYouTubeUrl(options.background);
            videoPreview.value = resolvePreview(options.background);
        } else if (isDirectImage(options.background)) {
            selectedImage.value = options.background;
        }
    }
    modal.value.open();
};

const close = () => {
    modal.value.close();
};

const reset = () => {
    selectedImage.value = null;
    videoUrl.value = '';
    videoPreview.value = null;
    selectedTransition.value = 'fade';
    existingBackground.value = null;
    existingTransition.value = 'fade';
};

const onImageSelected = (file) => {
    if (file && file.url) {
        selectedImage.value = file.url;
        videoUrl.value = '';
        videoPreview.value = null;
        existingBackground.value = null;
        emit('select', { type: 'image', value: file.url, transition: selectedTransition.value });
        close();
    }
};

const onVideoInput = () => {
    existingBackground.value = null;
    if (!videoUrl.value) {
        videoPreview.value = null;
        return;
    }

    videoPreview.value = resolvePreview(videoUrl.value);
};

// Resolve o que mostrar no preview: thumb do YouTube para links/ids,
// a própria URL para o resto (o @error esconde se não carregar).
// Antes caía num <img> quebrado para ids puros e URLs sem extensão.
const resolvePreview = (value) => {
    if (!value || typeof value !== 'string') return null;
    const videoId = getVideoId(value);
    if (videoId) {
        return `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
    }
    if (/^(https?:|data:|blob:)/.test(value)) return value;
    return null;
};

const isDirectImage = (value) => {
    if (!value || typeof value !== 'string') return false;
    if (/^(https?:|data:|blob:)/.test(value) === false) return false;
    return /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|#|$)/i.test(value)
        || /^data:image\//i.test(value);
};

// No longer needed: local getVideoId removed in favor of shared utility

const setVideoBackground = () => {
    if (videoUrl.value) {
        const vid = getVideoId(videoUrl.value) || videoUrl.value;
        const cleanUrl = getYouTubeUrl(videoUrl.value);
        emit('select', { type: 'video', value: cleanUrl, videoId: vid, transition: selectedTransition.value });
        close();
    } else if (existingBackground.value) {
        // Reaplica o fundo atual (ex. só trocou a transição)
        const isVideo = !!getVideoId(existingBackground.value);
        const type = isVideo ? 'video' : 'image';
        const val = isVideo ? getYouTubeUrl(existingBackground.value) : existingBackground.value;
        emit('select', { type, value: val, transition: selectedTransition.value });
        close();
    }
};

const removeVideoBackground = () => {
    emit('select', { type: 'remove' });
    close();
};

defineExpose({ open, close });
</script>
